import { useMemo, useState } from "react"
import styled from "styled-components"

import {
  Badge,
  Button,
  colors,
  colorScale,
  Skeleton,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"
import {
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfWeek,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { TimezoneSelect } from "src/components/Reports/TimezoneSelect"
import { RangeNavigation } from "src/components/TaskManagement/RangeNavigation"
import { TaskFilters } from "src/components/TaskManagement/TaskFilters"
import { TasksFilteredEmptyState } from "src/components/TaskManagement/TasksFilteredEmptyState"
import { IHomeFilterBody } from "src/data/homes/types/homeQueryTypes"
import { THome } from "src/data/homes/types/homeTypes"
import { useFetchSchedulerData } from "src/data/taskManagement/hooks/useFetchSchedulerData"
import { getTaskColors } from "src/data/taskManagement/taskManagementLogic"
import { TTimezone } from "src/data/timezones/timezones"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { InternalLink } from "src/ui/Link/InternalLink"
import { Scheduler } from "src/ui/Scheduler/Scheduler"
import {
  SCHEDULER_DATE_FORMATS,
  TSchedulerEvent,
  TSchedulerGridOptions,
  TSchedulerHeaderOptions,
  TSchedulerResource,
  TSchedulerViewType,
  WeekStartsOn,
} from "src/ui/Scheduler/schedulerTypes"
import { Show } from "src/ui/Show/Show"
import { getStoredSelectedTimezone, getWeekStart } from "src/utils/l10n"

export function TaskCalendarView() {
  const { clock_type } = useGetUser()
  const { t, langKeys } = useTranslate()
  const { timezone: storedTimezone } = getStoredSelectedTimezone()

  const now = toZonedTime(new Date(), storedTimezone.tzCode)

  const weekStartsOn = getWeekStart()

  const [homeIds, setHomeIds] = useState<string[]>([])
  const [homeGroupIds, setHomeGroupIds] = useState<string[]>([])
  const [dateRangeStart, setDateRangeStart] = useState(() => {
    return startOfWeek(now, { weekStartsOn })
  })

  const [viewType, setViewType] = useState<TSchedulerViewType>("week")

  const filters = useMemo((): IHomeFilterBody => {
    const homeIdsFilter = homeIds.length > 0 ? homeIds : undefined
    const homeGroupIdsFilter =
      homeGroupIds.length > 0 ? homeGroupIds : undefined

    return {
      limit: 15,
      offset: 0,
      ids: homeIdsFilter,
      homegroup_ids: homeGroupIdsFilter?.join(","),
    }
  }, [homeIds, homeGroupIds])

  const [selectedTimezone, setSelectedTimezone] =
    useState<TTimezone>(storedTimezone)

  const {
    homes,
    tasks,
    error,
    loading,
    loadMore,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchSchedulerData({
    homeFilters: filters,
    taskFilters: {
      limit: 500,
      offset: 0,
      sort_by: "earliest_start_date",
      sort_order: "asc",
    },
  })

  function getResources(homes: THome[]): TSchedulerResource[] {
    return homes.map(function (h) {
      return { id: h.home_id, name: h.name || "" }
    })
  }
  const resources: TSchedulerResource[] = getResources(homes)

  const events: TSchedulerEvent[] = useMemo(() => {
    const taskEvents = tasks
      .filter((task) => task.home_id) // Only include tasks with a home_id
      .map((task) => {
        const dueDate = new Date(task.time_window.due_date)
        const earliestStart = task.time_window.earliest_start_date
          ? new Date(task.time_window.earliest_start_date)
          : null

        // Use earliest start date if available, otherwise default to due date
        const startDate = earliestStart || dueDate
        const endDate = dueDate

        return {
          id: task.task_id,
          title: task.title,
          start: startOfDay(startDate),
          end: endDate,
          resourceId: task.home_id ?? "no-home",
          ...getTaskColors(task),
        }
      })

    return taskEvents
  }, [tasks])

  function getDateRangeEnd(
    dateRangeStart: Date,
    viewType: TSchedulerViewType,
    weekStartsOn: WeekStartsOn
  ) {
    if (viewType === "week") {
      return endOfWeek(dateRangeStart, { weekStartsOn })
    } else if (viewType === "month") {
      return endOfMonth(dateRangeStart)
    } else {
      return endOfYear(dateRangeStart)
    }
  }

  const dateRangeEnd = getDateRangeEnd(dateRangeStart, viewType, weekStartsOn)

  function goToPrevious() {
    switch (viewType) {
      case "week":
        setDateRangeStart(function (prev) {
          return subWeeks(prev, 1)
        })
        break
      case "month":
        setDateRangeStart(function (prev) {
          return subMonths(prev, 1)
        })
        break

      case "year":
        setDateRangeStart(function (prev) {
          return subYears(prev, 1)
        })
        break
    }
  }

  function goToNext() {
    switch (viewType) {
      case "week":
        setDateRangeStart(function (prev) {
          return addWeeks(prev, 1)
        })
        break
      case "month":
        setDateRangeStart(function (prev) {
          return addMonths(prev, 1)
        })
        break
      case "year":
        setDateRangeStart(function (prev) {
          return addYears(prev, 1)
        })
        break
    }
  }

  function getNavigationLabel(
    dateRangeStart: Date,
    dateRangeEnd: Date,
    viewType: TSchedulerViewType
  ) {
    switch (viewType) {
      case "week":
        return `${format(dateRangeStart, SCHEDULER_DATE_FORMATS.DAY_MONTH)} - ${format(dateRangeEnd, SCHEDULER_DATE_FORMATS.DAY_MONTH)}`
      case "month":
        return format(dateRangeStart, SCHEDULER_DATE_FORMATS.MONTH_YEAR)
      default:
        return format(dateRangeStart, SCHEDULER_DATE_FORMATS.YEAR)
    }
  }

  const navigationLabel = getNavigationLabel(
    dateRangeStart,
    dateRangeEnd,
    viewType
  )

  const headerOptions: TSchedulerHeaderOptions = {
    resourceTitle: t(langKeys.home),
    columnDividers: { show: false, color: colors.divider },
    headerDateStyles: {
      currentPeriodBackground: colors.neutral,
      currentPeriodBorderColor: colorScale.clarity[300],
      currentPeriodTextColor: colors.neutralPrimary,
      currentPeriodBorderRadius: 4,
    },
  }

  const gridOptions: TSchedulerGridOptions = {
    horizontalLines: { show: true, color: colors.divider },
    verticalLines: { show: true, color: colors.divider },
    highlightCurrentPeriod: {
      show: false,
      backgroundColor: `${colors.neutral}`,
    },
  }

  const hasNoTasksOnFilter = tasks.length === 0 && homes.length === 0

  if (error) {
    return (
      <Badge color="error">
        {t(langKeys.failed_general_error_try_refreshing_page)}
      </Badge>
    )
  }

  return (
    <div>
      <NavigationWrapper>
        <TaskFilters
          homeIds={homeIds}
          setHomeIds={setHomeIds}
          homeGroupIds={homeGroupIds}
          setHomeGroupIds={setHomeGroupIds}
        />

        <RangeNavigation
          onPrevious={goToPrevious}
          onNext={goToNext}
          label={navigationLabel}
          viewType={viewType}
          setViewType={setViewType}
          setDateRangeStart={setDateRangeStart}
          now={now}
          weekStartsOn={weekStartsOn}
        />
      </NavigationWrapper>

      <Show if={loading && !homes.length}>
        <SkeletonWrapper>
          <Skeleton variant="rect" height={50} />
          <Skeleton variant="rect" height={50} />
        </SkeletonWrapper>
      </Show>

      <Show if={!!homes.length}>
        <Scheduler
          resources={resources}
          events={events}
          startDate={dateRangeStart}
          endDate={dateRangeEnd}
          headerOptions={headerOptions}
          gridOptions={gridOptions}
          viewType={viewType}
          clockType={clock_type}
          timezone={selectedTimezone.tzCode}
          moreIndicatorText={(count) =>
            t(langKeys.plus_count_more_text, { count })
          }
          renderResourceLabel={(resource) => {
            return (
              <StyledInternalLink to={Routes.Home.location(resource.id)}>
                {resource.name}
              </StyledInternalLink>
            )
          }}
          height={600}
        />

        <Button
          variant="minimal"
          size="small"
          style={{ marginTop: spacing.M }}
          onClick={loadMore}
          disabled={!hasNextPage}
          loading={isFetchingNextPage}
        >
          {t(langKeys.load_more)}
        </Button>

        <TimezoneWrapper>
          <Text variant="body">{t(langKeys.timezone)}: </Text>
          <TimezoneSelect
            timezone={selectedTimezone}
            onChange={setSelectedTimezone}
          />
        </TimezoneWrapper>
      </Show>

      {hasNoTasksOnFilter && !loading && <TasksFilteredEmptyState />}
    </div>
  )
}

const NavigationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.M};
  flex-wrap: wrap;
  gap: ${spacing.S};
`

const TimezoneWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: ${spacing.M};
  align-items: center;
  gap: ${spacing.S};
`

const StyledInternalLink = styled(InternalLink)`
  width: 100%;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const SkeletonWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`
